import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowUUpLeft,
  ArrowUUpRight,
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  ListBullets,
  ListNumbers,
  MathOperations,
  Link,
  Image,
  Table,
  Code,
  ChatCentered,
  MagnifyingGlass,
  CaretDown,
  Minus,
} from "@phosphor-icons/react";
import { undo, redo } from "@codemirror/commands";
import { openSearchPanel } from "@codemirror/search";
import { useEditorStore } from "../../stores/editor";
import { Tooltip } from "../ui/Tooltip";
import { cn } from "../../lib/cn";
import {
  wrapSelection,
  insertSnippet,
  wrapBlock,
  toggleLinePrefix,
  insertTable,
} from "./editor-commands";

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

function TBtn({
  onClick,
  label,
  active,
  disabled,
  children,
}: {
  onClick: () => void;
  label: string;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip content={label} placement="bottom" delay={400}>
      <motion.button
        onClick={onClick}
        disabled={disabled}
        whileHover={disabled ? {} : { scale: 1.1 }}
        whileTap={disabled ? {} : { scale: 0.9 }}
        transition={spring}
        aria-label={label}
        className={cn(
          "flex h-[26px] w-[26px] items-center justify-center rounded-[var(--radius-sm)]",
          "transition-colors duration-75",
          disabled
            ? "text-[var(--text-muted)] opacity-30 cursor-not-allowed"
            : active
              ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
              : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
        )}
      >
        {children}
      </motion.button>
    </Tooltip>
  );
}

function Divider() {
  return <div className="mx-0.5 h-4 w-px bg-[var(--border-hairline)] shrink-0" />;
}

interface DropdownOption {
  label: string;
  value: string;
}

function ToolbarDropdown({
  options,
  value,
  onChange,
  label,
  width = 100,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <Tooltip content={label} placement="bottom" delay={400} disabled={open}>
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-[26px] items-center gap-0.5 rounded-[var(--radius-sm)] px-1.5",
            "text-[11px] font-medium text-[var(--text-muted)]",
            "hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
            "transition-colors duration-75",
          )}
          style={{ minWidth: width }}
        >
          <span className="truncate">{current?.label ?? label}</span>
          <CaretDown size={10} weight="bold" className="shrink-0 opacity-50" />
        </button>
      </Tooltip>
      {open && (
        <div
          className={cn(
            "absolute top-full left-0 z-50 mt-1 overflow-hidden",
            "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
            "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)]",
            "py-1",
          )}
          style={{ minWidth: width + 20 }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn(
                "flex w-full items-center px-3 py-1.5 text-[11px]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                opt.value === value
                  ? "text-[var(--text-primary)] font-semibold"
                  : "text-[var(--text-secondary)]",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STYLE_OPTIONS: DropdownOption[] = [
  { label: "Normal", value: "normal" },
  { label: "Section", value: "section" },
  { label: "Subsection", value: "subsection" },
  { label: "Subsubsection", value: "subsubsection" },
  { label: "Paragraph", value: "paragraph" },
];

const FONT_SIZE_OPTIONS: DropdownOption[] = [
  { label: "Tiny", value: "tiny" },
  { label: "Small", value: "small" },
  { label: "Normal", value: "normalsize" },
  { label: "Large", value: "large" },
  { label: "Larger", value: "Large" },
  { label: "Largest", value: "LARGE" },
  { label: "Huge", value: "huge" },
];

export function EditorToolbar() {
  const editorView = useEditorStore((s) => s.editorView);
  const disabled = !editorView;

  const run = useCallback(
    (fn: () => void) => {
      if (!editorView) return;
      fn();
    },
    [editorView],
  );

  const handleStyleChange = useCallback(
    (value: string) => {
      if (!editorView || value === "normal") return;
      wrapSelection(editorView, `\\${value}{`, "}");
    },
    [editorView],
  );

  const handleFontSizeChange = useCallback(
    (value: string) => {
      if (!editorView) return;
      wrapSelection(editorView, `{\\${value} `, "}");
    },
    [editorView],
  );

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 px-2 py-1 shrink-0",
        "border-b border-[var(--border-hairline)] bg-[var(--bg-surface)]",
        "overflow-x-clip overflow-y-visible",
      )}
    >
      {/* Undo / Redo */}
      <TBtn label="Undo (⌘Z)" disabled={disabled} onClick={() => run(() => undo(editorView!))}>
        <ArrowUUpLeft size={14} weight="bold" />
      </TBtn>
      <TBtn label="Redo (⌘⇧Z)" disabled={disabled} onClick={() => run(() => redo(editorView!))}>
        <ArrowUUpRight size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Style dropdown */}
      <ToolbarDropdown
        options={STYLE_OPTIONS}
        value="normal"
        onChange={handleStyleChange}
        label="Style"
        width={90}
      />

      <Divider />

      {/* Text formatting */}
      <TBtn label="Bold (⌘B)" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\textbf{", "}"))}>
        <TextB size={14} weight="bold" />
      </TBtn>
      <TBtn label="Italic (⌘I)" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\textit{", "}"))}>
        <TextItalic size={14} weight="bold" />
      </TBtn>
      <TBtn label="Underline (⌘U)" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\underline{", "}"))}>
        <TextUnderline size={14} weight="bold" />
      </TBtn>
      <TBtn label="Strikethrough" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\sout{", "}"))}>
        <TextStrikethrough size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Font size dropdown */}
      <ToolbarDropdown
        options={FONT_SIZE_OPTIONS}
        value="normalsize"
        onChange={handleFontSizeChange}
        label="Size"
        width={72}
      />

      <Divider />

      {/* Alignment */}
      <TBtn label="Align left" disabled={disabled} onClick={() => run(() => wrapBlock(editorView!, "flushleft"))}>
        <TextAlignLeft size={14} weight="bold" />
      </TBtn>
      <TBtn label="Center" disabled={disabled} onClick={() => run(() => wrapBlock(editorView!, "center"))}>
        <TextAlignCenter size={14} weight="bold" />
      </TBtn>
      <TBtn label="Align right" disabled={disabled} onClick={() => run(() => wrapBlock(editorView!, "flushright"))}>
        <TextAlignRight size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Lists */}
      <TBtn label="Bullet list" disabled={disabled} onClick={() => run(() => {
        const sel = editorView!.state.sliceDoc(editorView!.state.selection.main.from, editorView!.state.selection.main.to);
        const items = sel ? sel.split("\n").map((l) => `  \\item ${l}`).join("\n") : "  \\item ";
        insertSnippet(editorView!, `\\begin{itemize}\n${items}\n\\end{itemize}`);
      })}>
        <ListBullets size={14} weight="bold" />
      </TBtn>
      <TBtn label="Numbered list" disabled={disabled} onClick={() => run(() => {
        const sel = editorView!.state.sliceDoc(editorView!.state.selection.main.from, editorView!.state.selection.main.to);
        const items = sel ? sel.split("\n").map((l) => `  \\item ${l}`).join("\n") : "  \\item ";
        insertSnippet(editorView!, `\\begin{enumerate}\n${items}\n\\end{enumerate}`);
      })}>
        <ListNumbers size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Math */}
      <TBtn label="Inline math (⌘⇧M)" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "$", "$"))}>
        <MathOperations size={14} weight="bold" />
      </TBtn>
      <TBtn label="Display math" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\[\n", "\n\\]"))}>
        <span className="text-[10px] font-mono font-bold leading-none">∑</span>
      </TBtn>

      <Divider />

      {/* Insert */}
      <TBtn label="Insert link" disabled={disabled} onClick={() => run(() => {
        const sel = editorView!.state.sliceDoc(editorView!.state.selection.main.from, editorView!.state.selection.main.to);
        insertSnippet(editorView!, `\\href{url}{${sel || "link text"}}`, 6);
      })}>
        <Link size={14} weight="bold" />
      </TBtn>
      <TBtn label="Insert image" disabled={disabled} onClick={() => run(() => {
        insertSnippet(
          editorView!,
          "\\begin{figure}[h]\n  \\centering\n  \\includegraphics[width=0.8\\textwidth]{filename}\n  \\caption{Caption}\n  \\label{fig:label}\n\\end{figure}",
        );
      })}>
        <Image size={14} weight="bold" />
      </TBtn>
      <TBtn label="Insert table" disabled={disabled} onClick={() => run(() => insertTable(editorView!, 4, 3))}>
        <Table size={14} weight="bold" />
      </TBtn>
      <TBtn label="Horizontal rule" disabled={disabled} onClick={() => run(() => insertSnippet(editorView!, "\n\\noindent\\rule{\\textwidth}{0.4pt}\n"))}>
        <Minus size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Code / Comment / Search */}
      <TBtn label="Code / verbatim" disabled={disabled} onClick={() => run(() => wrapSelection(editorView!, "\\texttt{", "}"))}>
        <Code size={14} weight="bold" />
      </TBtn>
      <TBtn label="Toggle comment (⌘/)" disabled={disabled} onClick={() => run(() => toggleLinePrefix(editorView!, "% "))}>
        <ChatCentered size={14} weight="bold" />
      </TBtn>
      <TBtn label="Find & replace (⌘F)" disabled={disabled} onClick={() => run(() => openSearchPanel(editorView!))}>
        <MagnifyingGlass size={14} weight="bold" />
      </TBtn>
    </div>
  );
}
